import { FC } from 'react';
import { useTheme } from '@mui/material';
import {
  GAUGE_ANIMATION,
  GAUGE_COLORS,
  GAUGE_DIMENSIONS,
  GAUGE_VISUAL,
} from '../../constants';
import { GaugeSvgProps } from '../../types';

export const GaugeSvg: FC<GaugeSvgProps> = ({ value }) => {
  const theme = useTheme();

  // Function to get status color based on value
  const getStatusColor = (value: number): string => {
    if (value >= GAUGE_COLORS.SUCCESS_THRESHOLD) {
      return theme.palette.success.accent || theme.palette.success.main;
    }
    return theme.palette.error.main;
  };

  // Generate arc path
  const getArcPath = () => {
    return `M ${GAUGE_DIMENSIONS.START_X} ${GAUGE_DIMENSIONS.CENTER_Y} A ${GAUGE_DIMENSIONS.ARC_RADIUS} ${GAUGE_DIMENSIONS.ARC_RADIUS} 0 0 1 ${GAUGE_DIMENSIONS.END_X} ${GAUGE_DIMENSIONS.CENTER_Y}`;
  };

  // Generate dots
  const generateDots = () => {
    return Array.from({ length: GAUGE_VISUAL.DOT_COUNT }, (_, i) => {
      const angle =
        i * GAUGE_VISUAL.DOT_ANGLE_STEP + GAUGE_VISUAL.DOT_ANGLE_OFFSET;
      const radians = (angle * Math.PI) / 180;
      const cx =
        GAUGE_DIMENSIONS.CENTER_X +
        GAUGE_DIMENSIONS.INNER_RADIUS * Math.cos(radians);
      const cy =
        GAUGE_DIMENSIONS.CENTER_Y +
        GAUGE_DIMENSIONS.INNER_RADIUS * Math.sin(radians);

      return (
        <circle
          key={i}
          cx={cx}
          cy={cy}
          r={GAUGE_VISUAL.DOT_RADIUS}
          fill={theme.palette.white}
        />
      );
    });
  };

  return (
    <svg
      viewBox={`0 0 ${GAUGE_DIMENSIONS.VIEWBOX_WIDTH} ${GAUGE_DIMENSIONS.VIEWBOX_HEIGHT}`}
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Background arc */}
      <path
        d={getArcPath()}
        fill="none"
        stroke={theme.palette.background.blue}
        strokeWidth={theme.spacing(GAUGE_VISUAL.STROKE_WIDTH)}
        strokeLinecap="round"
      />
      {/* Progress arc */}
      <path
        d={getArcPath()}
        fill="none"
        strokeDasharray={`${(value * GAUGE_DIMENSIONS.ARC_LENGTH) / 100} ${GAUGE_DIMENSIONS.ARC_LENGTH}`}
        stroke={getStatusColor(value)}
        strokeWidth={theme.spacing(GAUGE_VISUAL.STROKE_WIDTH)}
        strokeLinecap="round"
        style={{
          transition: `stroke-dasharray ${GAUGE_ANIMATION.TRANSITION_DURATION} ${GAUGE_ANIMATION.TRANSITION_EASING}`,
        }}
      />
      {/* White dots on inner radius */}
      {generateDots()}
    </svg>
  );
};
